import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Tooltip,
  LinearProgress,
  Chip,
} from '@mui/material';
import {
  Login as LoginIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  Cloud as CloudIcon,
  Psychology as ScenarioIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  Settings as SettingsIcon,
  Security as SecurityIcon,
  BugReport as BugReportIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import api, { AuditLogEntry, Tenant } from '../../services/api';
import { usePermissions } from '../../contexts/PermissionContext';
import { PageTransition } from '../../components/PageTransition';
import { useTimezone } from '../../contexts/TimezoneContext';
import { formatUiDateTime } from '../../utils/dateUtils';

const EVENT_TYPE_ICONS: Record<string, React.ReactNode> = {
  login: <LoginIcon />,
  logout: <StopIcon />,
  partner_create: <BusinessIcon />,
  partner_update: <BusinessIcon />,
  partner_delete: <BusinessIcon />,
  tenant_create: <PeopleIcon />,
  tenant_update: <PeopleIcon />,
  tenant_delete: <PeopleIcon />,
  provider_create: <CloudIcon />,
  provider_update: <CloudIcon />,
  provider_delete: <CloudIcon />,
  scenario_create: <ScenarioIcon />,
  scenario_update: <ScenarioIcon />,
  scenario_delete: <ScenarioIcon />,
  execution_start: <PlayArrowIcon />,
  execution_stop: <StopIcon />,
  execution_config_update: <SettingsIcon />,
  attack_type_create: <BugReportIcon />,
  attack_type_update: <BugReportIcon />,
  attack_type_delete: <BugReportIcon />,
  allow_list_create: <SecurityIcon />,
  allow_list_update: <SecurityIcon />,
};

function getEventIcon(eventType: string): React.ReactNode {
  return EVENT_TYPE_ICONS[eventType] ?? <HistoryIcon />;
}

function formatTimestamp(ts: string | null, timeZone: string): string {
  if (!ts) return '—';
  return formatUiDateTime(ts, timeZone);
}

function truncate(str: string | null | undefined, max: number): string {
  if (!str) return '—';
  return str.length <= max ? str : `${str.slice(0, max)}…`;
}

const AuditLog: React.FC = () => {
  const { timezone } = useTimezone();
  const { isAdmin, hasPermission } = usePermissions();
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [tenantFilter, setTenantFilter] = useState<string>('');
  const [eventTypeFilter, setEventTypeFilter] = useState<string>('');
  const [fromTs, setFromTs] = useState<string>('');
  const [toTs, setToTs] = useState<string>('');
  const selectedTenant = (() => {
    try {
      const s = sessionStorage.getItem('selectedTenant');
      if (!s) return null;
      const t = JSON.parse(s);
      return t?.tenant_id ?? null;
    } catch {
      return null;
    }
  })();

  const fetchEntries = useCallback(async () => {
    if (!hasPermission('view_audit_log')) return;
    setLoading(true);
    try {
      const tenantId = isAdmin
        ? (tenantFilter === '' ? undefined : tenantFilter)
        : selectedTenant;
      if (!isAdmin && !tenantId) {
        setEntries([]);
        return;
      }
      const data = await api.getAuditLog({
        tenant_id: tenantId ?? null,
        from_ts: fromTs || undefined,
        to_ts: toTs || undefined,
        event_type: eventTypeFilter || undefined,
        limit: 200,
        offset: 0,
      });
      setEntries(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch audit log:', err);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, tenantFilter, eventTypeFilter, fromTs, toTs, selectedTenant, hasPermission]);

  useEffect(() => {
    if (isAdmin) {
      api.getTenants().then(setTenants).catch(() => setTenants([]));
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const eventTypes = React.useMemo(() => {
    const set = new Set(entries.map((e) => e.event_type).filter(Boolean));
    return Array.from(set).sort();
  }, [entries]);

  return (
    <PageTransition>
      <Box>
        <Typography variant="h6" gutterBottom>
          Audit Log
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Who did what, when, and from where. Tenant admins see only their tenant; super users can filter by tenant or view system-only events.
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
          {isAdmin && (
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Tenant</InputLabel>
              <Select
                value={tenantFilter}
                label="Tenant"
                onChange={(e) => setTenantFilter(e.target.value)}
              >
                <MenuItem value="">System only</MenuItem>
                {tenants.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Event type</InputLabel>
            <Select
              value={eventTypeFilter}
              label="Event type"
              onChange={(e) => setEventTypeFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {eventTypes.map((et) => (
                <MenuItem key={et} value={et}>
                  {et}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="From (ISO)"
            type="datetime-local"
            InputLabelProps={{ shrink: true }}
            value={fromTs}
            onChange={(e) => setFromTs(e.target.value)}
            sx={{ width: 220 }}
          />
          <TextField
            size="small"
            label="To (ISO)"
            type="datetime-local"
            InputLabelProps={{ shrink: true }}
            value={toTs}
            onChange={(e) => setToTs(e.target.value)}
            sx={{ width: 220 }}
          />
        </Box>

        {loading && <LinearProgress sx={{ mb: 2 }} />}

        <TableContainer component={Paper} variant="outlined">
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Time</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Tenant</TableCell>
                <TableCell>IP</TableCell>
                <TableCell>Browser</TableCell>
                <TableCell>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    No audit entries match the filters.
                  </TableCell>
                </TableRow>
              )}
              {entries.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {formatTimestamp(row.timestamp, timezone)}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={row.event_type}>
                      <Chip
                        size="small"
                        icon={<>{getEventIcon(row.event_type)}</>}
                        label={row.event_type}
                        variant="outlined"
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell>{row.action || '—'}</TableCell>
                  <TableCell>
                    {row.user_name || row.user_email || row.user_id || '—'}
                  </TableCell>
                  <TableCell>{row.tenant_name ?? (row.tenant_id ? row.tenant_id : 'System')}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{row.ip_address || '—'}</TableCell>
                  <TableCell>
                    <Tooltip title={row.user_agent || ''}>
                      <span>{truncate(row.user_agent, 40)}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {row.details && typeof row.details === 'object' ? (
                      <Tooltip title={JSON.stringify(row.details, null, 2)}>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {Object.keys(row.details).length} field(s)
                        </Typography>
                      </Tooltip>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </PageTransition>
  );
};

export default AuditLog;
